import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Markdown from './Markdown';
import usePageMeta from '../hooks/usePageMeta';
import JsonLd from './JsonLd';
import Footer from './Footer';
import BookCallModal from './BookCallModal';
import { getServiceBySlug } from '../data/services';
import servicesData from '../data/services';
import { SITE_URL, business } from '../data/business';
import './MarketingPage.css';

const ServicePage = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const [isModalOpen, setIsModalOpen] = useState(false);

  usePageMeta(
    service
      ? {
          title: `${service.title} | Second Life Software`,
          description: service.metaDescription,
          path: `/services/${service.slug}`,
        }
      : {}
  );

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const pageUrl = `${SITE_URL}/services/${service.slug}`;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.metaDescription,
    serviceType: service.name,
    url: pageUrl,
    provider: {
      '@type': 'ProfessionalService',
      '@id': `${SITE_URL}/#organization`,
      name: business.name,
      telephone: business.telephone,
      url: SITE_URL,
    },
    areaServed: business.areaServed.map((name) => ({ '@type': 'AdministrativeArea', name })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/services/${service.slug}` },
      { '@type': 'ListItem', position: 3, name: service.name, item: pageUrl },
    ],
  };

  const faqSchema = service.faq && service.faq.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: service.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  const related = servicesData.filter((s) => s.slug !== service.slug);

  return (
    <div className="mkt-page">
      <JsonLd id={`service-${service.slug}`} data={serviceSchema} />
      <JsonLd id={`service-breadcrumb-${service.slug}`} data={breadcrumbSchema} />
      {faqSchema && <JsonLd id={`service-faq-${service.slug}`} data={faqSchema} />}

      <header className="mkt-topbar">
        <Link to="/" className="mkt-logo">Second Life Software</Link>
        <button className="mkt-topbar-btn" onClick={() => setIsModalOpen(true)}>
          Book a Call
        </button>
      </header>

      <section className="mkt-hero">
        <span className="mkt-eyebrow">Services</span>
        <h1 className="mkt-h1">{service.title}</h1>
        <p className="mkt-sub">{service.intro}</p>
        <button className="mkt-cta-btn" onClick={() => setIsModalOpen(true)}>
          Book a Free Consultation
        </button>
      </section>

      <div className="mkt-body">
        <Markdown>{service.body}</Markdown>
      </div>

      {service.faq && service.faq.length > 0 && (
        <section className="mkt-faq">
          <h2>Frequently asked questions</h2>
          {service.faq.map((f, i) => (
            <div key={i} className="mkt-faq-item">
              <p className="mkt-faq-q">{f.q}</p>
              <p className="mkt-faq-a">{f.a}</p>
            </div>
          ))}
        </section>
      )}

      <section className="mkt-cta">
        <h2 className="mkt-cta-title">Have an idea you're ready to build?</h2>
        <p className="mkt-cta-text">
          Tell us what you're trying to build and we'll give you an honest,
          jargon-free scope and estimate. No obligation.
        </p>
        <button className="mkt-cta-btn" onClick={() => setIsModalOpen(true)}>
          Book a Free Call
        </button>
      </section>

      <nav className="mkt-related">
        <p className="mkt-related-title">Other services</p>
        <div className="mkt-related-links">
          {related.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`}>{s.name}</Link>
          ))}
        </div>
      </nav>

      <Footer />
      <BookCallModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default ServicePage;
